/**
 * Handle visited jobs highlighting
 *
 * Visited jobs are saved to session storage and marked with a CSS class. A `htmx:afterSwap` handler takes care of the
 * updating.
 *
 * @param storageKey Key for the session storage where the jobs are saved to
 * @param visitedClass CSS class for visited jobs
 * @param currentClass CSS class for currently displayed job
 */
class VisitedJobsHandler {
  readonly storageKey: string;

  readonly visitedClass: string;

  readonly currentClass: string;

  private visitedJobs = new Set<string>([]);

  constructor(storageKey = "visitedJobs", visitedClass = "visited-job", currentClass = "selected-job") {
    this.storageKey = storageKey;
    this.visitedClass = visitedClass;
    this.currentClass = currentClass;
  }

  /**
   * Load data from session storage, add the current job, update highlighting and add event listener
   */
  public init(): void {
    this.loadVisitedJobsFromSessionStorage();

    const articleElement = document.querySelector<HTMLDivElement>("article[data-job-id]");
    const {jobId} = articleElement.dataset;

    this.addVisitedJob(jobId);
    this.updateListHighlighting();

    document.addEventListener("htmx:afterSwap", () => this.updateListHighlighting());
  }

  /**
   * Update the highlighting in the sidebar to match the currently selected job
   *
   * Visited jobs are saved to sessionStorage
   */
  public updateListHighlighting(): void {
    // it's important that we fetch the element here, as it was replaced by HTMX and the old one is invalid
    const articleElement = document.querySelector<HTMLDivElement>("article[data-job-id]");
    const {jobId} = articleElement.dataset;

    document.querySelectorAll<HTMLLIElement>("aside.jobs-aside li").forEach(jobCard => {
      if (jobCard.classList.contains(this.currentClass) || this.visitedJobs.has(jobCard.dataset.jobId)) {
        jobCard.classList.add(this.visitedClass);
      }

      jobCard.classList.toggle(this.currentClass, jobCard.dataset.jobId === jobId);
    });

    this.addVisitedJob(jobId);
  }

  private loadVisitedJobsFromSessionStorage(): void {
    const ids = JSON.parse(sessionStorage.getItem(this.storageKey)) as Array<string> | null;

    if (!ids) {
      return;
    }

    ids.forEach(id => {
      this.visitedJobs.add(id);
    });
  }

  private writeVisitedJobsToSessionStorage(): void {
    sessionStorage.setItem(this.storageKey, JSON.stringify([...this.visitedJobs]));
  }

  private addVisitedJob(jobId: string): void {
    // do nothing if job is already in list
    if (this.visitedJobs.has(jobId)) {
      return;
    }

    this.visitedJobs.add(jobId);
    this.writeVisitedJobsToSessionStorage();
  }
}

export default VisitedJobsHandler;
